<template>
  <Dialog
    v-if="fixedTrialFlow && !needsActivation"
    :visible="visible"
    modal
    :closable="!isSubmitting"
    @update:visible="$emit('update:visible', $event)">
    <template #header>Publish this project</template>
    <p v-if="!activatedForPublish">Publishing during your trial does not enable paid renewal or charge your card.</p>
    <p v-if="submitError" role="alert">{{ submitError }}</p>
    <Button :disabled="isSubmitting" label="Publish changes" @click="publishExistingProject" />
  </Dialog>
  <Dialog
    v-else-if="!fixedTrialFlow"
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '52rem', maxWidth: 'calc(100vw - 1rem)' }"
    @update:visible="$emit('update:visible', $event)">
    <template #header>
      <div class="flex w-full items-start gap-4">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <h2 class="text-xl/[1.3] font-semibold text-heading">
            {{ trialDays === null ? 'Choose a plan to go live' : 'Start your free trial to go live' }}
          </h2>
          <p class="text-sm/normal text-copy">Choose a plan and your changes will be published immediately.</p>
          <p v-if="selectedPlanRecommendationCopy" class="mt-1 text-sm text-copy">
            {{ selectedPlanRecommendationCopy }}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          title="Close"
          class="flex size-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-overlay hover:text-heading"
          @click="$emit('update:visible', false)">
          <XMarkIcon class="size-[18px]" />
        </button>
      </div>
    </template>

    <div class="space-y-6">
      <div class="flex justify-center">
        <div class="inline-flex items-center gap-1 rounded-full border border-line bg-overlay p-1">
          <button
            type="button"
            :class="[
              'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
              !isAnnual ? 'bg-[var(--rm-app-card)] text-heading shadow-xs' : 'text-copy hover:text-heading'
            ]"
            @click="isAnnual = false">
            Monthly
          </button>
          <button
            type="button"
            :class="[
              'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
              isAnnual ? 'bg-[var(--rm-app-card)] text-heading shadow-xs' : 'text-copy hover:text-heading'
            ]"
            @click="isAnnual = true">
            Annual
            <span class="ml-1 text-xs font-medium text-positive">Save 20%</span>
          </button>
        </div>
      </div>

      <p class="text-center text-sm text-muted">
        Annual is selected by default; switch to Monthly if you prefer month-to-month billing.
      </p>

      <div class="grid gap-4 sm:grid-cols-3">
        <button
          v-for="plan in displayPlans"
          :key="plan.tier"
          type="button"
          :aria-pressed="selectedPlan === plan.tier"
          :class="[
            'relative rounded-[10px] border bg-[var(--rm-app-card)] p-5 text-left transition-colors',
            selectedPlan === plan.tier ? 'border-primary ring-1 ring-primary' : 'border-line hover:border-line-strong'
          ]"
          @click="selectPlan(plan.tier)">
          <div
            v-if="plan.tier === recommendedTier && recommendedTier !== 'grow'"
            class="absolute -top-3 left-1/2 -translate-x-1/2">
            <span class="inline-flex items-center rounded-md bg-primary px-2 py-1 text-xs font-bold text-white">
              Recommended
            </span>
          </div>
          <div
            v-else-if="plan.display?.popular && recommendedTier === 'grow'"
            class="absolute -top-3 left-1/2 -translate-x-1/2">
            <span class="inline-flex items-center rounded-md bg-primary px-2 py-1 text-xs font-bold text-white">
              Most popular
            </span>
          </div>

          <div class="text-base font-semibold text-heading">
            {{ plan.name }}
          </div>
          <p class="mt-1 text-xs text-muted">
            {{ plan.display?.tagline }}
          </p>
          <div class="mt-4 flex items-baseline gap-x-1">
            <span class="text-3xl font-bold tracking-tight text-heading">
              {{ isAnnual ? plan.display?.yearly_price : plan.display?.monthly_price }}
            </span>
            <span class="text-sm text-muted">/mo</span>
          </div>
          <p v-if="isAnnual" class="mt-1 text-xs text-muted">Billed annually ({{ plan.display?.yearly_total }}/yr)</p>

          <ul class="mt-4 space-y-1.5 text-xs">
            <li
              v-for="feature in plan.display?.features ?? []"
              :key="feature"
              class="flex items-start gap-x-1.5 text-copy">
              <CheckIcon class="mt-0.5 size-3 flex-none text-primary" />
              {{ feature }}
            </li>
          </ul>

          <div
            v-if="selectedPlan === plan.tier"
            class="absolute top-3 right-3 flex size-5 items-center justify-center rounded-full bg-primary text-white">
            <CheckIcon class="size-3" />
          </div>
        </button>
      </div>

      <div v-if="displayPlans.length === 0" class="rounded-lg border border-line bg-danger-soft px-4 py-3">
        <p class="text-sm text-heading">
          {{ emptyPlansError }}
        </p>
      </div>

      <div
        v-if="missingItems.length > 0"
        data-test="publish-subscribe-plan-warning"
        class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
        <ExclamationTriangleIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
        <div class="text-sm text-copy">
          <p class="font-semibold text-heading">Your setup requires {{ TIER_NAMES[recommendedTier] }} or higher</p>
          <p class="mt-0.5">
            You're using {{ joinItems(missingItems.map((item) => item.label)) }}, which
            {{ missingItems.length > 1 ? 'are' : 'is' }} not included in the selected plan. You can still choose this
            plan, but {{ missingItems.length > 1 ? 'those features' : 'that feature' }} won't be available.
          </p>
        </div>
      </div>

      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <label for="publish-subscribe-card-element" class="text-sm font-semibold text-heading"
            >Payment information</label
          >
          <span class="inline-flex items-center gap-1 text-xs text-muted">
            <LockClosedIcon class="size-3.5" aria-hidden="true" />
            Secure payment via Stripe
          </span>
        </div>
        <div class="rounded-md border border-line bg-[var(--rm-app-card)] p-3">
          <div id="publish-subscribe-card-element" class="min-h-[24px]">
            <!-- Stripe card element mounts here -->
          </div>
        </div>
        <div v-if="stripeError" class="text-sm text-danger">
          {{ stripeError }}
        </div>
      </div>

      <div
        v-if="trialDays === null"
        data-test="publish-subscribe-charge"
        class="flex items-start gap-3 rounded-lg border border-line bg-overlay px-4 py-3">
        <CreditCardIcon class="mt-0.5 size-4 shrink-0 text-copy" aria-hidden="true" />
        <div class="text-sm text-copy">
          <p class="font-semibold text-heading">{{ chargeToday }} charged today</p>
          <p class="mt-0.5">{{ chargeExplanation }} Cancel anytime in Billing.</p>
        </div>
      </div>
      <div
        v-else
        data-test="publish-subscribe-trial"
        class="flex items-start gap-3 rounded-lg border border-line bg-positive-soft px-4 py-3">
        <ShieldCheckIcon class="mt-0.5 size-4 shrink-0 text-positive" aria-hidden="true" />
        <div class="text-sm text-copy">
          <p class="font-semibold text-heading">Your card won't be charged for {{ trialDays }} days</p>
          <p class="mt-0.5">
            Cancel anytime before it ends and you won't be charged. You can upgrade your plan at any time.
          </p>
        </div>
      </div>

      <div v-if="submitError" role="alert" class="rounded-lg border border-line bg-danger-soft px-4 py-3">
        <p class="text-sm text-heading">
          {{ submitError }}
        </p>
      </div>

      <p v-if="showCardDetailsHelper" class="text-sm text-copy">
        Enter card details to start {{ trialDays === null ? 'your plan' : 'the trial' }} and publish.
      </p>

      <button
        type="button"
        :disabled="isSubmitDisabled"
        class="flex min-h-10 w-full items-center justify-center gap-2 rounded-md bg-action px-4 text-sm font-semibold text-[var(--rm-on-action)] transition-colors hover:bg-action-strong disabled:cursor-not-allowed disabled:opacity-50"
        @click="handleSubscribeAndPublish">
        <ArrowPathIcon v-if="isSubmitting" class="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {{ submitLabel }}
      </button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js'
import { CheckIcon } from '@heroicons/vue/20/solid'
import {
  ArrowPathIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import api from '@dashboard/services/axiosApi'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import {
  TIER_NAMES,
  itemsAboveTier,
  joinItems,
  requiredTier,
  tierRank
} from '@dashboard/components/billing/planPreselection'
import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { isPublishStillRunningError, useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { storeToRefs } from 'pinia'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { computed, nextTick, onUnmounted, ref, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { ProjectStart, SetupRequirements, TrialActivation } from '@dashboard/types/user'

type PublishPlan = {
  tier: string
  name: string
  monthly_price_id: string | null
  yearly_price_id: string | null
  tagline?: string
  popular?: boolean
  features?: string[]
  monthlyPrice?: string
  annualPrice?: string
  annualTotal?: string
  display?: {
    monthly_price?: string | null
    yearly_price?: string | null
    yearly_total?: string | null
    tagline?: string | null
    popular?: boolean | null
    features?: string[] | null
  } | null
}

type SubscribeAndPublishResponse = { subscription_created?: boolean; trial_ends_at?: string | null }

type PendingAuthenticatedPayment = {
  paymentIntentId: string
  priceId: string
}

const props = defineProps<{
  visible: boolean
  publishMessage?: string | null
  plans?: PublishPlan[]
  /** From availablePlans; undefined when the plans came without them (402 publish fallback). */
  setupRequirements?: SetupRequirements | null
  /** From availablePlans or the publish 402; undefined when the caller has none and this modal loads it. */
  projectStart?: ProjectStart | null
  activationRecovery?: TrialActivation | null
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'subscribed'): void
}>()

const route = useRoute()
const teamPid = String(route.params.teamPid)
let disposed = false
const isCurrentProject = () => !disposed && String(route.params.teamPid) === teamPid
const notificationStore = useNotificationStore()
const configStore = useConfigStore()
const { config, currentTeam, lastChecksum } = storeToRefs(configStore)
const { trialActivation, isTrialing, projectTrialDays } = storeToRefs(useSubscriptionStore())
const billingDialog = useBillingDialogStore()
const recoveryTrial = computed(() => props.activationRecovery ?? trialActivation.value)
const fixedTrialFlow = ref(false)
const activatedForPublish = ref(false)
// A fixed-end trial that must be activated (expired, or a confirmed activation to resume) before publishing.
const needsActivation = computed(
  () =>
    fixedTrialFlow.value &&
    (!isTrialing.value || !!recoveryTrial.value?.pending_activation) &&
    !activatedForPublish.value
)
const requirements = ref<SetupRequirements | null>(null)
const startTerms = ref<ProjectStart | null>(null)
// Only the server knows whether this project's subscription trials (ENG-1600); the owner payload answers until
// availablePlans does.
const trialDays = computed(() => (startTerms.value ? startTerms.value.trial_days : projectTrialDays.value))

const isAnnual = ref(true)
const selectedPlan = ref('')
const stripeError = ref('')
const submitError = ref('')
const isSubmitting = ref(false)
const stripeReady = ref(false)
const autoSelectedPlan = ref('')
const pendingAuthenticatedPayment = ref<PendingAuthenticatedPayment | null>(null)

// Stripe elements
let stripe: Stripe | null = null
let elements: StripeElements | null = null
let cardElement: StripeCardElement | null = null
let stripeInitialized = false

const emptyPlansError = 'Plan details could not be loaded. Close this window and try publishing again.'

const displayPlans = computed(() =>
  [...(props.plans ?? [])].sort((left, right) => tierRank(left.tier) - tierRank(right.tier))
)

const selectedPlanRecord = computed(() => displayPlans.value.find((entry) => entry.tier === selectedPlan.value) ?? null)

const planTagline = (plan: PublishPlan | null) => {
  if (!plan) {
    return ''
  }
  return plan.display?.tagline ?? plan.tagline ?? ''
}

const selectedPlanRecommendationCopy = computed(() => {
  if (!selectedPlanRecord.value || !autoSelectedPlan.value || selectedPlan.value !== autoSelectedPlan.value) {
    return ''
  }

  const planName = selectedPlanRecord.value.name
  const reason =
    selectedPlanRecord.value.tier === 'scale'
      ? 'it includes website personalization and conversion goals.'
      : `${planTagline(selectedPlanRecord.value).replace(/\.*$/, '') || 'it best fits the setup you just built'}.`

  const before = trialDays.value === null ? 'you publish' : 'starting the trial'
  return `We recommend ${planName} for this setup because ${reason} You can choose another plan before ${before}.`
})

const selectedPriceId = computed(() => {
  const plan = selectedPlanRecord.value
  return plan ? (isAnnual.value ? plan.yearly_price_id : plan.monthly_price_id) : null
})
// What publishing charges today for the selected price, after any agency discount.
const chargeToday = computed(() => {
  const priceId = selectedPriceId.value
  const plan = selectedPlanRecord.value
  return (
    (priceId ? startTerms.value?.charge_today[priceId] : undefined) ??
    (isAnnual.value ? plan?.display?.yearly_total : plan?.display?.monthly_price) ??
    'Your plan price'
  )
})
const chargeExplanation = computed(() => {
  const discount = startTerms.value?.agency_discount_percent ?? null
  return discount === null
    ? 'Only an organization’s first project gets a free trial, so this project’s plan starts when you publish.'
    : `Agency projects are billed from the start. This includes your ${discount}% agency discount.`
})
const submitLabel = computed(() => {
  if (trialDays.value === null) {
    return isSubmitting.value ? 'Setting up your plan…' : `Pay ${chargeToday.value} & publish`
  }
  return isSubmitting.value ? 'Setting up your trial…' : `Start ${trialDays.value}-day trial & publish`
})

const showCardDetailsHelper = computed(() => !stripeReady.value && !isSubmitting.value && displayPlans.value.length > 0)

const selectPlan = (tier: string) => {
  selectedPlan.value = tier
  autoSelectedPlan.value = ''
}

const isSubmitDisabled = computed(() => isSubmitting.value || !stripeReady.value || displayPlans.value.length === 0)

const recommendedTier = computed(() => requiredTier(requirements.value))
// Setup items the selected plan doesn't include, named by the server.
const missingItems = computed(() =>
  selectedPlanRecord.value ? itemsAboveTier(selectedPlanRecord.value.tier, requirements.value) : []
)

const loadRequirements = async () => {
  if (props.setupRequirements !== undefined) {
    requirements.value = props.setupRequirements
  }
  if (props.projectStart !== undefined) {
    startTerms.value = props.projectStart
  }
  if (props.setupRequirements !== undefined && props.projectStart !== undefined) {
    return
  }
  try {
    const { data } = await api.get(`/api/${teamPid}/billing/availablePlans`)
    if (isCurrentProject()) {
      if (props.setupRequirements === undefined) {
        requirements.value = data.setup_requirements ?? null
      }
      if (props.projectStart === undefined) {
        startTerms.value = data.project_start ?? null
      }
    }
  } catch {
    // The requirements only drive the recommendation and warning; the plan can still start without them, and
    // the owner payload still says whether it trials.
    if (props.setupRequirements === undefined) {
      requirements.value = null
    }
  }
}

// Pre-select the cheapest plan that covers the setup, at least Scale.
const selectRecommendedPlan = () => {
  const recommended = tierRank(recommendedTier.value) > tierRank('scale') ? recommendedTier.value : 'scale'
  selectedPlan.value =
    displayPlans.value.find((entry) => entry.tier === recommended)?.tier ?? displayPlans.value[0]?.tier ?? ''
  autoSelectedPlan.value = selectedPlan.value
}

const initializeStripe = async () => {
  if (stripeInitialized) {
    return
  }

  try {
    const { data } = await api.get('/api/stripe/publishable-key')
    if (!isCurrentProject()) {
      return
    }
    const loadedStripe = await loadStripe(data.key)
    if (!isCurrentProject()) {
      return
    }
    stripe = loadedStripe

    if (!stripe) {
      throw new Error('Failed to load Stripe')
    }

    const isDarkMode = document.documentElement.classList.contains('dark')

    elements = stripe.elements()
    cardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: isDarkMode ? '#e5e7eb' : '#111827',
          '::placeholder': {
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }
        },
        invalid: {
          color: isDarkMode ? '#fca5a5' : '#fa755a'
        }
      }
    })

    stripeInitialized = true
    await mountCardElement()
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
    submitError.value = 'Failed to load payment form. Please try again.'
  }
}

const mountCardElement = async () => {
  if (!cardElement) {
    return
  }

  await nextTick()

  let attempts = 0
  const maxAttempts = 10

  const tryMount = () => {
    if (!isCurrentProject()) {
      return true
    }
    const el = document.getElementById('publish-subscribe-card-element')
    if (el && cardElement) {
      cardElement.mount(el)
      cardElement.on('change', (event) => {
        stripeError.value = event.error?.message || ''
        stripeReady.value = event.complete
      })
      return true
    }
    return false
  }

  if (tryMount()) {
    return
  }

  const interval = setInterval(() => {
    attempts++
    if (tryMount() || attempts >= maxAttempts) {
      clearInterval(interval)
    }
  }, 100)
}

const submitSubscribeAndPublish = async (paymentMethodId: string, priceId: string) => {
  if (!isCurrentProject()) {
    throw new Error('Project changed. Reopen billing for the current project.')
  }
  const response = await api.post(`/api/${teamPid}/billing/subscribe-and-publish`, {
    payment_method_id: paymentMethodId,
    price_id: priceId,
    data: toRaw(config.value),
    checksum: lastChecksum.value,
    publish_message: props.publishMessage || undefined
  })

  return response.data as SubscribeAndPublishResponse
}
const completeSubscribeAndPublish = async (paymentIntentId: string, priceId: string) => {
  if (!isCurrentProject()) {
    throw new Error('Project changed. Reopen billing for the current project.')
  }
  const response = await api.post(`/api/${teamPid}/billing/subscribe-and-publish`, {
    payment_intent_id: paymentIntentId,
    price_id: priceId,
    data: toRaw(config.value),
    checksum: lastChecksum.value,
    publish_message: props.publishMessage || undefined
  })

  return response.data as SubscribeAndPublishResponse
}

const finishSubscribedAndPublished = async (result: SubscribeAndPublishResponse) => {
  if (!isCurrentProject()) {
    return
  }
  let notificationMessage = 'Your changes are now live!'
  if (result.subscription_created !== false) {
    notificationMessage = result.trial_ends_at
      ? 'Your trial has started and your changes are now live!'
      : 'Your plan has started and your changes are now live!'
  }

  await configStore.configQuery.refetch()
  if (!isCurrentProject()) {
    return
  }
  recordAnalyticsEvent(AnalyticsEventTypes.PUBLISHED)
  if (!currentTeam.value?.last_published_at) {
    recordAnalyticsEvent(AnalyticsEventTypes.FIRST_PUBLISH)
  }

  notificationStore.addNotification(notificationMessage)

  emit('subscribed')
  emit('update:visible', false)
}

const subscribeErrorMessage = (error: any) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  `Failed to start ${trialDays.value === null ? 'your plan' : 'your trial'}. Please try again.`

const publishExistingProject = async () => {
  if (!isCurrentProject() || isSubmitting.value) {
    return
  }
  isSubmitting.value = true
  submitError.value = ''
  try {
    await configStore.publish(props.publishMessage ?? '')
    if (!isCurrentProject()) {
      return
    }
    emit('subscribed')
    emit('update:visible', false)
  } catch (error: any) {
    if (!isCurrentProject()) {
      return
    }
    // The publish is still running on the server; the store already says so and refetches.
    if (isPublishStillRunningError(error)) {
      emit('subscribed')
      emit('update:visible', false)
      return
    }
    submitError.value = error?.response?.data?.message || 'Publishing failed. Please try again.'
    await configStore.configQuery.refetch()
  } finally {
    isSubmitting.value = false
  }
}

const handleProjectActivated = async () => {
  if (!isCurrentProject()) {
    return
  }
  activatedForPublish.value = true
  await publishExistingProject()
}

const handleSubscribeAndPublish = async () => {
  if (!isCurrentProject()) {
    return
  }
  if (!stripe || !cardElement) {
    submitError.value = 'Payment form not ready. Please wait a moment and try again.'
    return
  }

  isSubmitting.value = true
  submitError.value = ''

  let paymentMethodId: string | null = null
  let priceId: string | null = null

  if (pendingAuthenticatedPayment.value) {
    try {
      const pendingPayment = pendingAuthenticatedPayment.value
      const subscribeResult = await completeSubscribeAndPublish(pendingPayment.paymentIntentId, pendingPayment.priceId)
      await finishSubscribedAndPublished(subscribeResult)
      pendingAuthenticatedPayment.value = null
    } catch (error: any) {
      submitError.value = subscribeErrorMessage(error)
    } finally {
      isSubmitting.value = false
    }
    return
  }

  try {
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    })
    if (!isCurrentProject()) {
      return
    }

    if (error) {
      stripeError.value = error.message || 'Payment method creation failed'
      return
    }

    if (!paymentMethod?.id) {
      submitError.value = 'Payment method creation failed. Please try another card.'
      return
    }

    paymentMethodId = paymentMethod.id

    priceId = selectedPriceId.value

    if (!priceId) {
      submitError.value = 'Could not determine plan price. Please try again.'
      return
    }

    const subscribeResult = await submitSubscribeAndPublish(paymentMethodId, priceId)
    await finishSubscribedAndPublished(subscribeResult)
  } catch (error: any) {
    if (!isCurrentProject()) {
      return
    }
    if (
      error.response?.status === 402 &&
      error.response?.data?.requires_action &&
      error.response?.data?.payment_intent_client_secret &&
      error.response?.data?.payment_intent_id &&
      paymentMethodId &&
      priceId
    ) {
      if (!stripe) {
        submitError.value =
          'Your bank requires extra authentication, but the payment form is not ready. Please reload and try again.'
        return
      }

      submitError.value = ''

      const confirmation = await stripe.confirmCardPayment(error.response.data.payment_intent_client_secret)
      if (!isCurrentProject()) {
        return
      }
      if (confirmation.error) {
        submitError.value =
          confirmation.error.message ||
          'Your bank could not verify the card. Please try another card or contact your bank.'
        return
      }

      pendingAuthenticatedPayment.value = {
        paymentIntentId: error.response.data.payment_intent_id,
        priceId
      }

      try {
        const subscribeResult = await completeSubscribeAndPublish(error.response.data.payment_intent_id, priceId)
        await finishSubscribedAndPublished(subscribeResult)
        pendingAuthenticatedPayment.value = null
      } catch (retryError: any) {
        submitError.value = subscribeErrorMessage(retryError)
      }
      return
    }

    submitError.value = subscribeErrorMessage(error)
  } finally {
    isSubmitting.value = false
  }
}

// Initialize Stripe when modal becomes visible
watch(
  () => props.visible,
  async (newVal) => {
    if (newVal) {
      fixedTrialFlow.value = !!recoveryTrial.value
      activatedForPublish.value = false
      submitError.value = ''
      stripeError.value = ''
      if (fixedTrialFlow.value) {
        if (needsActivation.value) {
          billingDialog.open({ mode: 'keep', source: 'publish', onSuccess: handleProjectActivated })
        }
        return
      }
      await loadRequirements()
      if (!isCurrentProject()) {
        return
      }
      selectRecommendedPlan()
      await initializeStripe()

      // Re-mount if already initialized (modal was closed and reopened)
      if (stripeInitialized && cardElement) {
        await nextTick()
        await mountCardElement()
      }
    }
  },
  { immediate: true }
)

// Dismissing the activation dialog without activating abandons this publish.
watch(
  () => billingDialog.visible,
  (open) => {
    if (!open && props.visible && needsActivation.value) {
      emit('update:visible', false)
    }
  }
)

// Reset Stripe when modal closes so it re-initializes with current styles
watch(
  () => props.visible,
  (newVal) => {
    if (!newVal && cardElement) {
      cardElement.destroy()
      cardElement = null
      elements = null
      stripeInitialized = false
      stripeReady.value = false
    }
  }
)

onUnmounted(() => {
  disposed = true
  if (cardElement) {
    cardElement.destroy()
  }
})
</script>
